import { PropsWithChildren } from 'react';
import Image from 'next/image';
import Link, { LinkProps } from 'next/link';
import { INotification } from '@/types/api/notifications';
import { getFirstValueTrue } from '@/utils/arrayUtils';
import {
  separateWordFromString,
  toUppercaseFirstLetter,
} from '@/utils/stringUtils';
import { Badge, Box, Typography } from '@mui/material';

interface NotificationModalRowProps {
  notification: INotification;
  isRead?: boolean;
  truncMessage?: boolean;
}

const Wrapper = ({ children, ...rest }: PropsWithChildren<LinkProps>) => {
  return rest.href ? <Link {...rest}>{children}</Link> : <>{children}</>;
};

const NotificationModalRow = ({
  notification,
  isRead,
  truncMessage = false,
}: NotificationModalRowProps) => {
  const { message, created_at, target_url, category } = notification;
  const {
    word: author,
    backString: backMessage,
    frontString: frontMessage,
  } = separateWordFromString(message, 'você');

  const calculateNotificationTime = (notificationCreate: string) => {
    const milliSeconds =
      new Date().valueOf() - new Date(notificationCreate).valueOf();

    const daysInMonth = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      0
    ).getDate();

    const minutes = Math.trunc(milliSeconds / 60000);
    const hours = Math.trunc(minutes / 60);
    const days = Math.trunc(hours / 24);

    const month = Math.trunc(days / daysInMonth);

    return getFirstValueTrue([
      minutes === 1 && `${minutes} minuto`,
      minutes < 60 && `${minutes} minutos`,
      hours === 1 && `${hours} hora`,
      hours < 24 && `${hours} horas`,
      days < daysInMonth && `${days} dias`,
      days === 1 && `${days} dia`,
      month === 1 && `${month} mês`,
      `${month} meses`,
    ]);
  };

  return (
    <Wrapper href={target_url || ''} passHref>
      <Box
        component={target_url ? 'a' : 'span'}
        target="_blank"
        data-testid="notification-row"
        className={target_url ? 'link' : ''}
        sx={{
          display: 'flex',
          alignItems: 'center',
          '&.link': {
            cursor: 'pointer',
          },
          '&.link:hover': {
            transition: 'all 150ms ease-in-out',
            backgroundColor: '#49c7ff21',
            '& .notification-text': {
              textDecorationLine: 'underline',
            },
          },
          '& + &': {
            borderTop: '1px solid #EBEBEB',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            overflow: 'hidden',
            py: 2,
            px: 4,
          }}
        >
          <Badge
            variant="dot"
            invisible={isRead}
            sx={{
              position: 'relative',
              '& .MuiBadge-badge': {
                backgroundColor: 'warning.main',
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                color: 'warning.dark',
                top: '8px',
                left: '-10px',
              },
            }}
          >
            <Box
              sx={{
                width: '40px',
                height: '40px',
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                position: 'relative',
              }}
            >
              <Image
                src={category.icon}
                width="24"
                height="24"
                objectFit="contain"
                alt={`Icone de notificação da categoria ${category.name}`}
              />
            </Box>
          </Badge>

          <Box
            sx={{
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                color: '#686868',
                textAlign: 'left',
              }}
            >
              <Typography
                variant="body2"
                role="presentation"
                data-testid="notification-message"
                className="notification-text"
                sx={{
                  whiteSpace: truncMessage ? 'nowrap' : 'wrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {backMessage}
                <Typography
                  color="primary"
                  data-testid="notification-author"
                  variant="subtitle2"
                  component="strong"
                  sx={{
                    lineHeight: '22px',
                  }}
                >
                  {toUppercaseFirstLetter(author)}
                </Typography>{' '}
                {frontMessage}
              </Typography>
              <Typography
                variant="caption"
                data-testid="notification-creation-date"
              >
                Há {calculateNotificationTime(created_at)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Wrapper>
  );
};

export type { NotificationModalRowProps };
export default NotificationModalRow;
